import React, { useEffect, useRef, useState } from 'react'
import { useProject } from 'hooks/useProject'
import { useHubFolders } from 'hooks/useHubFolders'
import { useStableHandler } from 'hooks/useStableHandler'
import { IContextMenuState } from 'types'
import { useApplicationStore } from 'hooks/application'
import { Link, generatePath, useLocation, useMatch } from 'react-router-dom'
import ResourceMenu, { CreateActions } from 'components/sidebar/components/resourcemenu'
import NewProcess from 'components/document/components/modal/new'
import NewTable from 'components/spreadsheet/components/newTable'
import Templates from 'components/templates'
import { Plus } from 'components/icons'
import Button from 'components/button'
import {
  SIDEBAR_SURFACE,
  SIDEBAR_ROW,
  SIDEBAR_ICON_SEMI,
  SIDEBAR_HEADER_PAD,
  SIDEBAR_HERO_BADGE,
  SIDEBAR_ACTION,
  SIDEBAR_ROW_MUTED,
  SIDEBAR_ROW_PAD,
  sidebarLabel,
  dropRingStyle,
  indentFor,
  SelectionJoin
} from 'components/sidebar/surface'
import HubBadge from 'components/sidebar/hubBadge'
import { hubHomePath } from 'components/sidebar/hubHome'
import { belowButton, rowButtonProps } from 'components/sidebar/rowButton'
import SidebarTree, { SidebarTreeView } from 'components/sidebar/components/folders/SidebarTree'
import { RenameResult } from 'components/sidebar/components/folders/rowParts'
import SidebarDnd, { SidebarDragView, SidebarDropRoot } from 'components/sidebar/components/folders/SidebarDnd'
import HubOptionsMenu from 'components/sidebar/components/folders/HubOptionsMenu'
import { useSidebarSort } from 'components/sidebar/components/folders/useSidebarSort'
import { resourcePath } from 'components/sidebar/components/folders/ResourceRow'
import { joinOf, useSidebarSelection } from 'components/sidebar/components/folders/selection'
import FolderMenu, {
  RowMenuTarget,
  contextMenuPosition,
  focusFirstRow
} from 'components/sidebar/components/folders/FolderMenu'
import { useExpandedFolders } from 'components/sidebar/components/folders/useExpandedFolders'
import {
  SidebarNode,
  buildSidebarTree,
  findNode,
  parentIds,
  folderAncestorIds,
  subfolderFits,
  visibleRowIds
} from 'components/sidebar/components/folders/tree'
import { SETTINGS, DOCUMENT, TABLE, HUB_DEPENDENCIES } from 'routes'
import Skeleton from 'components/skeleton'
import Tooltip from 'components/tooltip'

// Label widths for placeholder rows, cycled so they read like a list of names.
const ROW_SKELETON_WIDTHS = ['72%', '54%', '64%', '80%', '48%', '60%']

/** Placeholder rows, shaped like resource rows, for a list that's still loading. */
const RowsSkeleton: React.FC<{ layer: number; count: number }> = ({ layer, count }) => (
  <div role="status" aria-label="Loading resources" data-testid="sidebar-rows-skeleton">
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="flex items-center gap-2 w-full h-8 pr-2" style={{ paddingLeft: indentFor(layer) }}>
        <span className="flex items-center justify-center w-6 pl-1.5 shrink-0">
          <Skeleton className="block h-4 w-4 rounded" />
        </span>
        <Skeleton className="block h-3" style={{ width: ROW_SKELETON_WIDTHS[index % ROW_SKELETON_WIDTHS.length] }} />
      </div>
    ))}
  </div>
)

/** The + menu that's open: where it creates (a folder, or null for the top level) and the + that opened it. */
interface CreateMenu {
  folderId: string | null
  opener: HTMLElement | null
  top: number
  left: number
}

/** A create dialog that's open, and the folder what it creates goes in (null = top level). */
interface CreateIn {
  folderId: string | null
}

/** The folder, document or table showing its rename box; `isNew` for the first rename of a folder just created. */
interface Renaming {
  id: string
  isNew: boolean
}

const CREATE_MENU_WIDTH = 200

const Folders: React.FC = () => {
  const { project, projectAdmin, projectOwner } = useProject()
  const { renameFolder, moveItems, createFolder, deleteFolder, renameResource } = useHubFolders()
  const { sidebarWidth } = useApplicationStore()
  // The shell keeps this panel mounted while the sidebar is hidden; the rows are skipped until it reopens.
  const sidebarOpen = sidebarWidth > 0
  const location = useLocation()

  const [createMenu, setCreateMenu] = useState<CreateMenu | null>(null)
  const [newDocumentIn, setNewDocumentIn] = useState<CreateIn | null>(null)
  const [newTableIn, setNewTableIn] = useState<CreateIn | null>(null)
  const [templatesIn, setTemplatesIn] = useState<CreateIn | null>(null)
  const [renaming, setRenaming] = useState<Renaming | null>(null)
  const [rowMenu, setRowMenu] = useState<RowMenuTarget | null>(null)
  // What an archive or delete in a row's menu would take away, while it's pointed at or confirmed: red.
  const [previewIds, setPreviewIds] = useState<string[] | null>(null)
  const { sort, setSort, foldersFirst, setFoldersFirst } = useSidebarSort(project.publicId)
  // Holds the hub's + button. The Tooltip around the button takes the button's own ref.
  const addButtonHolder = useRef<HTMLDivElement>(null)

  const resourcesLoading = project.loading
  // Hub Owners manage folders and can move anything. Anyone else can move (drag, select, Move to…),
  // rename and archive only the documents and tables they own; the rest of their tree is read-only,
  // though they can still expand and collapse it. Owners and Builders create documents and tables,
  // in any folder they can see.
  const canManage = projectOwner && !project.isDeleted
  const canCreate = projectAdmin && !project.isDeleted
  // The documents and tables this viewer owns (hub Owners own them all). The server's `canMove` is
  // exactly that: Owner on the resource, the role renaming and archiving it also need.
  const ownedIds = new Set(
    project.isDeleted ? [] : project.sidebarResources.filter((r) => r.canMove).map((r) => r.publicId)
  )
  const isMovable = (id: string): boolean => canManage || ownedIds.has(id)
  // A drag can start and land somewhere: the drop targets are the rows this viewer can see.
  const canDrop = canManage || ownedIds.size > 0
  const folderIds = new Set(project.hubFolders.map((f) => f.publicId))
  const resourceById = new Map(project.sidebarResources.map((r) => [r.publicId, r]))
  // A folder's menu is for whoever can create in it or manage it; the owner-only rows show
  // disabled for a Builder. A document's or table's is for whoever can open or move it.
  const allowsMenu = (kind: SidebarNode['kind'], id: string, href: string | null): boolean =>
    kind === 'folder' ? canManage || canCreate : isMovable(id) || href !== null
  const hasMenu = (id: string): boolean => {
    if (folderIds.has(id)) return allowsMenu('folder', id, null)
    const resource = resourceById.get(id)
    return resource !== undefined && allowsMenu(resource.kind, id, resourcePath(project.publicId, resource))
  }

  const { loaded, expanded, toggle, expand, collapseAll } = useExpandedFolders(
    project.publicId,
    project.hubFolders,
    !project.loading
  )
  const tree = buildSidebarTree(project.hubFolders, project.sidebarResources, sort, foldersFirst)
  // The shared order, where a reorder made while sorted (or with folders first) goes.
  const manualTree =
    sort === 'manual' && !foldersFirst ? tree : buildSidebarTree(project.hubFolders, project.sidebarResources)
  // The rows on screen, top to bottom: what Shift ranges over and all a selection may hold.
  const visible = visibleRowIds(tree, expanded)
  // What a selection, and so a drag or Move to…, may carry.
  const movableVisible = visible.filter(isMovable)
  const documentMatch = useMatch(DOCUMENT)
  const tableMatch = useMatch(TABLE)
  // The document or table the route is showing.
  const activeId = documentMatch?.params.processId ?? tableMatch?.params.tableId ?? null
  // The open row is highlighted, so a selection starts from it. A row's menu, and any dialog it
  // opened, holds the selection: a click elsewhere just closes them.
  // A selection keeps to one folder's rows, or the top level's.
  const parents = parentIds(tree)
  const selection = useSidebarSelection(
    canDrop,
    movableVisible,
    activeId,
    rowMenu !== null,
    (id) => parents.get(id) ?? null
  )
  // Renaming and archiving act on one row: not on a row that is part of a selection of several.
  const inMultiSelection = (id: string): boolean => selection.selected.length > 1 && selection.isSelected(id)
  const previewed = new Set(previewIds ?? [])
  const isPreviewed = (id: string): boolean => previewed.has(id)
  // The rows drawn as a block: what an archive or delete would take, while it shows, or else the selection.
  const marked = previewed.size > 0 ? isPreviewed : selection.isSelected
  // A run of marked rows draws as one block: each squares the corners it shares with a marked
  // neighbour on screen.
  const selectionJoin = (id: string): SelectionJoin => joinOf(visible, id, marked)
  const canRename = (id: string): boolean => ownedIds.has(id) && !inMultiSelection(id)

  // A rename box whose row has left the screen (a refresh took the folder away, or its parent was
  // closed) is over: the box unmounted without saving, and mustn't come back with the row.
  if (renaming && loaded && !project.loading && !visible.includes(renaming.id)) setRenaming(null)

  // A selection, rename or open menu belongs to the hub it was made in.
  useEffect(() => {
    selection.clear()
    setRenaming(null)
    setRowMenu(null)
    setCreateMenu(null)
  }, [project.publicId])

  // Once per page, open the folders above the page on screen and scroll its row into view. This
  // waits for the hub's resources, because on a first load the route arrives before them. Keyed
  // on the resource rather than the path, so switching a table's view doesn't reopen a folder
  // the user has just closed.
  const handledRoute = useRef<string | null>(null)
  const pendingScroll = useRef<string | null>(null)
  useEffect(() => {
    if (!loaded) return
    const route = `${project.publicId}:${activeId ?? location.pathname}`
    if (handledRoute.current === route) return
    const active = activeId ? project.sidebarResources.find((r) => r.publicId === activeId) : undefined
    if (activeId && !active) return
    handledRoute.current = route
    if (!active) return
    expand(folderAncestorIds(project.hubFolders, active.folderId))
    pendingScroll.current = active.publicId
  }, [loaded, location.pathname, project.sidebarResources])

  // Runs after every render, because the row only exists once the folders above it have opened.
  // The scroll stays pending until its frame actually runs: a render in between cancels the frame
  // and the next run of this effect books another.
  useEffect(() => {
    const id = pendingScroll.current
    const row = id ? document.getElementById(id) : null
    if (!row) return
    const handle = requestAnimationFrame(() => {
      // In case the frame still runs after the row has gone (a replaced row, or an environment
      // where cancelling doesn't take).
      if (!row.isConnected) return
      pendingScroll.current = null
      row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    return () => cancelAnimationFrame(handle)
  })

  /**
   * Creates a "New folder" under `parentId` (null = top level) and opens it for renaming. The parent
   * opens so the new row is on screen; the rename box's focus scrolls it into view.
   */
  const newFolder = (parentId: string | null): void => {
    if (!canManage) return
    createFolder('New folder', parentId).then((created) => {
      if (!created) return
      if (parentId) expand([parentId])
      setRenaming({ id: created.publicId, isNew: true })
    })
  }

  /**
   * A folder made a moment ago and left unnamed (Escape, or a blank name) wasn't wanted: it goes
   * again, and focus goes to the folder it was in, or the tree.
   */
  const discardNewFolder = (folderId: string): void => {
    const parentId = project.hubFolders.find((f) => f.publicId === folderId)?.parentId ?? null
    deleteFolder(folderId).then(
      () => focusFirstRow([parentId, null]),
      // deleteFolder has shown the error; the folder stays, as named.
      () => undefined
    )
  }

  const createActions: CreateActions = {
    newFolder,
    newDocument: (folderId) => setNewDocumentIn({ folderId }),
    newTable: (folderId) => setNewTableIn({ folderId }),
    newFromTemplate: (folderId) => setTemplatesIn({ folderId })
  }

  const openRowMenu = (id: string, opener: HTMLElement, at: { top: number; left: number }): void => {
    const node = findNode(tree, id)
    if (!node) return
    setRowMenu({
      id,
      kind: node.kind,
      name: node.name,
      href: node.kind === 'folder' ? null : resourcePath(project.publicId, node.resource),
      row: opener,
      ...at
    })
  }

  /**
   * A right-click opens the row's menu on a folder that has one, and on a document or table the
   * viewer can move. Any other row keeps the browser's menu, with its own Open in new tab; its ⋯
   * still opens the sidebar's.
   */
  const onRowContextMenu = useStableHandler((event: React.MouseEvent<HTMLElement>, id: string, dragging: boolean) => {
    // A rename box keeps the browser's menu, for copy and paste.
    if (renaming?.id === id) return
    if (!(folderIds.has(id) ? hasMenu(id) : isMovable(id))) return
    event.preventDefault()
    // No menu over a drag in progress.
    if (dragging) return
    openRowMenu(id, event.currentTarget, contextMenuPosition(event))
  })

  /**
   * The + menu hands focus back to the + that opened it as it closes, however it closes. A choice
   * that opens a modal or a rename box moves focus on from there. A click outside closes Menu on
   * mousedown, before the browser moves focus to what was clicked, so this doesn't take focus away
   * from it. Keep Menu's close on mousedown: closing on click would pull focus back to + afterwards.
   */
  const closeCreateMenu = (): void => {
    const opener = createMenu?.opener
    setCreateMenu(null)
    if (opener?.isConnected) opener.focus({ preventScroll: true })
  }

  // The rows' callbacks close over state that changes on most renders (the selection, the rows on
  // screen, the rename), so each keeps one identity and calls the latest: the rows are memoised,
  // and a new callback would re-render every one of them.
  const toggleFolder = useStableHandler(toggle)
  const selectClick = useStableHandler(selection.handleRowClick)
  const startRename = useStableHandler((id: string) => {
    if (folderIds.has(id) ? canManage : canRename(id)) setRenaming({ id, isNew: false })
  })
  const endRename = useStableHandler((id: string, { name, discarded }: RenameResult) => {
    setRenaming(null)
    const resource = resourceById.get(id)
    if (resource) {
      // A blank or unchanged name changes nothing.
      if (name) renameResource(resource, name)
      return
    }
    const wasNew = renaming?.id === id && renaming.isNew
    if (name) renameFolder(id, name)
    else if (wasNew && discarded) discardNewFolder(id)
  })
  const newIn = useStableHandler((folderId: string, button: HTMLElement) =>
    setCreateMenu({ folderId, opener: button, ...belowButton(button) })
  )
  const openMenu = useStableHandler((id: string, button: HTMLElement) => openRowMenu(id, button, belowButton(button)))

  // Built once per render, not per drag change: a hover re-renders the tree through renderTree, and
  // only the drag's fields change then.
  const baseView: Omit<SidebarTreeView, keyof SidebarDragView> = {
    hubId: project.publicId,
    brandColour: project.primaryColour,
    activeId,
    expanded,
    canManage,
    canDrop,
    isMovable,
    canRename,
    renamingId: renaming?.id ?? null,
    toggle: toggleFolder,
    isSelected: selection.isSelected,
    hasSelection: selection.selected.length > 0,
    selectionJoin,
    isPreviewed,
    selectClick,
    startRename,
    endRename,
    onContextMenu: onRowContextMenu,
    newIn: canCreate ? newIn : undefined,
    openMenu,
    hasMenu
  }

  const renderTree = (drag: SidebarDragView): React.ReactNode => {
    // A drop to the end of the top level, which has no row of its own to ring, rings the whole tree.
    const rootTarget = drag.dropIndicator?.rowId === null
    return (
      // A drop target itself, behind the rows: during a drag it grows some empty space below them,
      // which drops at the end of the top level.
      <SidebarDropRoot
        enabled={canDrop}
        className={`rounded-md outline-none ${drag.dragging ? 'pb-8' : ''}`}
        style={rootTarget ? dropRingStyle(project.primaryColour) : undefined}
        data-testid="sidebar-tree"
        data-tour="sidebar-tree"
        // Where focus goes when the row it was on has gone (a deleted top-level folder).
        data-sidebar-tree
        tabIndex={-1}
        role="group"
        aria-label="Hub resources"
        data-drop-target={rootTarget}
        onClick={(event) => {
          // A click on the tree's empty space, rather than on a row, clears the selection.
          if (event.target === event.currentTarget) selection.clear()
        }}
      >
        {sidebarOpen && !resourcesLoading && <SidebarTree nodes={tree} view={{ ...baseView, ...drag }} />}
      </SidebarDropRoot>
    )
  }

  const createMenuState: IContextMenuState = {
    open: createMenu !== null,
    top: `${createMenu?.top ?? 0}px`,
    left: `${createMenu?.left ?? 0}px`,
    right: 'auto',
    bottom: 'auto'
  }

  // The sidebar shell owns the scrolling. A second scroller here claimed the
  // full height whether or not the content needed it, which left the panel
  // scrollable with nothing to scroll.
  return (
    <div className="select-none" data-tour="sidebar-folders">
      {/* pr-1: its buttons end where a folder row's do. */}
      <div
        className={`sticky relative flex text-body-soft w-full font-semibold flex-row justify-center transition-all items-center box-border ${SIDEBAR_HEADER_PAD} pr-1 h-10 mb-1.5 top-0 z-[1]`}
        style={{ backgroundColor: SIDEBAR_SURFACE }}
      >
        <div className="flex items-center w-full gap-0">
          {/* The hub identifies its own resource list — badge, name and the
              two actions that belong to it. Replaces a generic "Resources"
              label that said nothing the hub's name doesn't. */}
          {/* Clicking the hub takes you back to its front page. */}
          {project.loading ? (
            <div className="flex items-center gap-2 min-w-0 flex-1" data-testid="sidebar-hub-skeleton">
              <Skeleton
                className="block shrink-0 rounded-md"
                style={{ width: SIDEBAR_HERO_BADGE, height: SIDEBAR_HERO_BADGE }}
              />
              <Skeleton className="block h-3.5 w-[120px] min-w-0" />
            </div>
          ) : (
            <Link to={hubHomePath(project)} className="flex items-center min-w-0" data-testid="sidebar-hub-home-link">
              <HubBadge
                name={project.name}
                colour={project.primaryColour}
                textColour={project.fontColour}
                size={SIDEBAR_HERO_BADGE}
              />
              <Tooltip content={project.name} showOnlyIfTruncated delay={300}>
                <div className="truncate text-[15px] font-semibold tracking-[-0.015em] text-body min-w-0 ml-2">
                  {project.name}
                </div>
              </Tooltip>
            </Link>
          )}

          <div
            className="flex items-center shrink-0"
            style={{
              marginLeft: 'auto'
            }}
          >
            <div
              ref={addButtonHolder}
              className="flex items-center shrink-0"
              onClick={(event) => {
                // The `+` button is hidden while loading; keep the create menu from
                // opening too, mirroring the Search row's loading guard.
                if (project.loading || !canCreate) return
                const rect = event.currentTarget.getBoundingClientRect()
                setCreateMenu({
                  folderId: null,
                  opener: addButtonHolder.current?.querySelector('button') ?? null,
                  top: rect.bottom + 10,
                  left: rect.left
                })
              }}
            >
              {!project.loading && canCreate && (
                <Tooltip content="New">
                  <Button
                    data-tour="add-resource"
                    aria-label="New"
                    className={SIDEBAR_ACTION}
                    internalType="transparent"
                  >
                    <Plus />
                  </Button>
                </Tooltip>
              )}
            </div>
            {/* At the far right, after +. Everyone gets it: sorting and expanding folders are the viewer's
                own, whatever they can do in the hub. */}
            {!project.loading && (
              <HubOptionsMenu
                relationshipsPath={
                  projectOwner && !project.isDeleted
                    ? generatePath(HUB_DEPENDENCIES, { projectId: project.publicId })
                    : undefined
                }
                membersPath={
                  projectOwner && !project.isDeleted
                    ? `${generatePath(SETTINGS, { projectId: project.publicId })}?tab=members`
                    : undefined
                }
                settingsPath={projectAdmin ? generatePath(SETTINGS, { projectId: project.publicId }) : undefined}
                sort={sort}
                onSortChange={setSort}
                foldersFirst={foldersFirst}
                onFoldersFirstChange={setFoldersFirst}
                hasFolders={project.hubFolders.length > 0}
                expandAll={() => expand(project.hubFolders.map((f) => f.publicId))}
                collapseAll={collapseAll}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full">
        {/* Only a viewer who can move something can drag (the other rows are disabled, and the
            sensors are off for anyone who can't), but it's always rendered, so gaining the right
            doesn't remount the tree. */}
        <SidebarDnd
          enabled={canDrop}
          hubId={project.publicId}
          tree={tree}
          manualTree={manualTree}
          folders={project.hubFolders}
          expanded={expanded}
          visible={movableVisible}
          sort={sort}
          onSortToManual={() => setSort('manual')}
          foldersFirst={foldersFirst}
          onFoldersFirstOff={() => setFoldersFirst(false)}
          selected={selection.selected}
          brandColour={project.primaryColour}
          expand={expand}
          moveItems={moveItems}
          onDrop={selection.clear}
        >
          {renderTree}
        </SidebarDnd>
        {resourcesLoading && <RowsSkeleton layer={0} count={6} />}
      </div>
      {!resourcesLoading && canCreate && (
        <div className="mt-1 flex flex-col">
          <div
            className={`${SIDEBAR_ROW} ${SIDEBAR_ROW_PAD} ${SIDEBAR_ROW_MUTED}`}
            {...rowButtonProps(() => setNewDocumentIn({ folderId: null }))}
          >
            <span className={SIDEBAR_ICON_SEMI}>
              <Plus />
            </span>
            <span className={sidebarLabel()}>New document</span>
          </div>
          <div
            className={`${SIDEBAR_ROW} ${SIDEBAR_ROW_PAD} ${SIDEBAR_ROW_MUTED}`}
            {...rowButtonProps(() => setNewTableIn({ folderId: null }))}
          >
            <span className={SIDEBAR_ICON_SEMI}>
              <Plus />
            </span>
            <span className={sidebarLabel()}>New table</span>
          </div>
          <div
            className={`${SIDEBAR_ROW} ${SIDEBAR_ROW_PAD} ${SIDEBAR_ROW_MUTED}`}
            {...rowButtonProps(() => setTemplatesIn({ folderId: null }))}
          >
            <span className={SIDEBAR_ICON_SEMI}>
              <Plus />
            </span>
            <span className={sidebarLabel()}>New from template</span>
          </div>
        </div>
      )}

      {createMenu && canCreate && (
        <ResourceMenu
          id="new-resource-menu"
          menuState={createMenuState}
          setMenuState={(state) => {
            if (!state.open) closeCreateMenu()
          }}
          width={CREATE_MENU_WIDTH}
          folderId={createMenu.folderId}
          canManage={canManage}
          canCreate={canCreate}
          folderFits={subfolderFits(tree, createMenu.folderId)}
          actions={createActions}
        />
      )}

      {/* On what the viewer may do rather than on the row being there, so a menu whose row a refresh
          takes away can close itself and put focus somewhere sensible. */}
      {rowMenu && allowsMenu(rowMenu.kind, rowMenu.id, rowMenu.href) && (
        <FolderMenu
          target={rowMenu}
          tree={tree}
          folders={project.hubFolders}
          selected={selection.selected}
          visible={movableVisible}
          canManage={canManage}
          canCreate={canCreate}
          canMove={isMovable(rowMenu.id)}
          ownsRow={ownedIds.has(rowMenu.id)}
          inSelection={inMultiSelection(rowMenu.id)}
          createActions={createActions}
          onClose={() => setRowMenu(null)}
          onRename={startRename}
          onSelectionMoved={selection.clear}
          onPreview={setPreviewIds}
        />
      )}

      {newDocumentIn && (
        <NewProcess
          id="new-process-modal"
          open
          setOpen={(open) => {
            if (!open) setNewDocumentIn(null)
          }}
          folderId={newDocumentIn.folderId}
        />
      )}
      {newTableIn && (
        <NewTable
          id="new-table-modal"
          open
          setOpen={(open) => {
            if (!open) setNewTableIn(null)
          }}
          folderId={newTableIn.folderId}
        />
      )}
      {templatesIn && (
        <Templates
          id="hub-templates-modal"
          open
          setOpen={(open) => {
            if (!open) setTemplatesIn(null)
          }}
          folderId={templatesIn.folderId}
        />
      )}
    </div>
  )
}

export default Folders
